/**
 * Dialogs do COFRE: criar/editar cofre (Nome + editor de permissões por
 * usuário), exclusões com confirmação (cofre/pasta/senha), pastas
 * (criar/renomear) e o Histórico (auditoria GET /vault/:id/logs).
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import type { Vault, VaultBody, VaultEntry, VaultFolder, VaultPermission, VaultPermissionLevel } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { useAuthUser } from '@/auth/use-auth';
import { teamListQuery } from '@/features/team/team-queries';
import { vaultLogsOptions, vaultTreeOptions } from './vault-queries';
import {
  useCreateVault,
  useCreateVaultFolder,
  useDeleteVault,
  useDeleteVaultEntry,
  useDeleteVaultFolder,
  useUpdateVault,
  useUpdateVaultFolder,
} from './vault-mutations';
import { formatDateTimeBR, LogActionBadge, vaultErrorMessage } from './vault-format';

// ── Cofre: criar/editar (Nome + permissões) ─────────────────────────────────

interface PermRow {
  userId: string;
  level: VaultPermissionLevel;
}

function VaultForm({
  title,
  initialName,
  initialPermissions,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  title: string;
  initialName: string;
  initialPermissions: VaultPermission[];
  pending: boolean;
  error: unknown;
  onSubmit: (body: VaultBody) => void;
  onClose: () => void;
}) {
  const me = useAuthUser();
  // a equipe deste tenant, inativos inclusive (quem já tem acesso segue com
  // nome); o cofre não se abre para login de portal nem para outra empresa, e a
  // API recusa acrescentar quem está fora (pedido da PoolTrack, 05/10)
  const usersQ = useQuery(teamListQuery({ includeInactive: true }));
  const users = usersQ.data?.items ?? [];

  const [name, setName] = useState(initialName);
  const [rows, setRows] = useState<PermRow[]>(
    initialPermissions.map((p) => ({ userId: String(p.userId), level: p.level }))
  );

  // Opções da linha i: usuários ativos ainda não usados nas OUTRAS linhas.
  // Mantém o usuário já selecionado (mesmo inativo) e omite a mim (o criador
  // já tem Edição implícita; admins têm Edição em tudo).
  // Quem já tinha acesso e está FORA da equipe (login de portal, outra empresa)
  // segue na linha com o nome e a marca, para poder ser tirado — a API não o
  // recusa por já estar lá, só não deixa acrescentar ninguém de fora.
  const initialNames = new Map(initialPermissions.map((p) => [String(p.userId), p.userName]));
  const optionsFor = (index: number): Array<{ id: number; name: string }> => {
    const current = rows[index]?.userId ?? '';
    const options: Array<{ id: number; name: string }> = users.filter((u) => {
      const idStr = String(u.id);
      if (idStr === current) return true;
      if (!u.active || u.id === me?.id) return false;
      return !rows.some((r, i) => i !== index && r.userId === idStr);
    });
    if (current !== '' && !usersQ.isPending && !options.some((u) => String(u.id) === current)) {
      const name = initialNames.get(current) ?? `Usuário #${current}`;
      options.unshift({ id: Number(current), name: `${name} (fora da equipe)` });
    }
    return options;
  };

  const setRow = (index: number, patch: Partial<PermRow>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const valid = name.trim() !== '' && rows.every((r) => r.userId !== '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit({
      name: name.trim(),
      permissions: rows.map((r) => ({ userId: Number(r.userId), level: r.level })),
    });
  };

  return (
    <Dialog open onClose={onClose} title={title}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="vault-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="vault-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Servidores, Acessos do cliente X…"
            required
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Permissões</Label>
            <button
              type="button"
              onClick={() => setRows((prev) => [...prev, { userId: '', level: 'read' }])}
              className="flex items-center gap-1 text-[13px] font-medium text-primary transition-colors hover:text-primary-pressed"
            >
              <Plus className="size-3.5" />
              Adicionar permissão
            </button>
          </div>

          {rows.length === 0 ? (
            <p className="rounded-md border border-dashed px-3 py-2.5 text-sm text-muted-foreground">
              Só você (e administradores) terá acesso a este cofre.
            </p>
          ) : (
            <div className="space-y-2">
              {rows.map((row, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Select
                    value={row.userId}
                    onChange={(e) => setRow(index, { userId: e.target.value })}
                    disabled={usersQ.isPending}
                    aria-label="Usuário da permissão"
                    className="h-8 min-w-0 flex-1"
                  >
                    <option value="">
                      {usersQ.isPending ? 'Carregando…' : 'Selecione o usuário…'}
                    </option>
                    {optionsFor(index).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Select>
                  <Select
                    value={row.level}
                    onChange={(e) => setRow(index, { level: e.target.value as VaultPermissionLevel })}
                    aria-label="Nível da permissão"
                    className="h-8 w-28 shrink-0"
                  >
                    <option value="read">Leitura</option>
                    <option value="write">Edição</option>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Remover permissão"
                    title="Remover permissão"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <X />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Leitura permite ver e copiar senhas; Edição também cria, altera e exclui.
          </p>
        </div>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {vaultErrorMessage(error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={pending || !valid}>
            {pending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Salvar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function NewVaultDialog({
  onCreated,
  onClose,
}: {
  onCreated: (vaultId: number) => void;
  onClose: () => void;
}) {
  const create = useCreateVault();
  return (
    <VaultForm
      title="Novo cofre"
      initialName=""
      initialPermissions={[]}
      pending={create.isPending}
      error={create.error}
      onClose={onClose}
      onSubmit={(body) =>
        create.mutate(body, {
          onSuccess: (vault) => {
            onCreated(vault.id);
            onClose();
          },
        })
      }
    />
  );
}

/** Editar cofre: as permissões atuais vêm do GET /vault/:id/tree. */
export function EditVaultDialog({ vault, onClose }: { vault: Vault; onClose: () => void }) {
  const treeQ = useQuery(vaultTreeOptions(vault.id));
  const update = useUpdateVault();

  if (treeQ.isPending) {
    return (
      <Dialog open onClose={onClose} title="Editar cofre">
        <div className="flex h-24 items-center justify-center">
          <Spinner />
        </div>
      </Dialog>
    );
  }
  if (treeQ.isError) {
    return (
      <Dialog open onClose={onClose} title="Editar cofre">
        <p className="text-sm text-destructive" role="alert">
          {vaultErrorMessage(treeQ.error)}
        </p>
      </Dialog>
    );
  }

  return (
    <VaultForm
      title="Editar cofre"
      initialName={treeQ.data.vault.name}
      initialPermissions={treeQ.data.permissions}
      pending={update.isPending}
      error={update.error}
      onClose={onClose}
      onSubmit={(body) => update.mutate({ vaultId: vault.id, body }, { onSuccess: onClose })}
    />
  );
}

export function DeleteVaultDialog({ vault, onClose }: { vault: Vault; onClose: () => void }) {
  const remove = useDeleteVault();
  return (
    <Dialog open onClose={onClose} title="Excluir cofre">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja excluir o cofre{' '}
          <span className="font-medium text-foreground">{vault.name}</span>? Apenas cofres vazios
          podem ser excluídos.
        </p>
        {remove.error ? (
          <p className="text-sm text-destructive" role="alert">
            {vaultErrorMessage(remove.error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={() => remove.mutate(vault.id, { onSuccess: onClose })}
          >
            {remove.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Pastas ──────────────────────────────────────────────────────────────────

/** Criar (com parentId da navegação atual) ou renomear pasta. */
export function VaultFolderDialog({
  vaultId,
  parentId,
  folder,
  onClose,
}: {
  vaultId: number;
  parentId: number | null;
  folder?: VaultFolder;
  onClose: () => void;
}) {
  const create = useCreateVaultFolder(vaultId);
  const update = useUpdateVaultFolder();
  const mutation = folder ? update : create;
  const [name, setName] = useState(folder?.name ?? '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (folder) {
      update.mutate(
        { folderId: folder.id, body: { name: trimmed, parentId: folder.parentId } },
        { onSuccess: onClose }
      );
    } else {
      create.mutate({ name: trimmed, parentId }, { onSuccess: onClose });
    }
  };

  return (
    <Dialog open onClose={onClose} title={folder ? 'Renomear pasta' : 'Nova pasta'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="vault-folder-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="vault-folder-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Produção, Bancos, Wi-Fi…"
            required
            autoFocus
          />
        </div>
        {mutation.error ? (
          <p className="text-sm text-destructive" role="alert">
            {vaultErrorMessage(mutation.error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={mutation.isPending || !name.trim()}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function DeleteVaultFolderDialog({
  folder,
  onDeleted,
  onClose,
}: {
  folder: VaultFolder;
  onDeleted?: () => void;
  onClose: () => void;
}) {
  const remove = useDeleteVaultFolder();
  return (
    <Dialog open onClose={onClose} title="Excluir pasta">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja excluir a pasta{' '}
          <span className="font-medium text-foreground">{folder.name}</span>? Apenas pastas vazias
          podem ser excluídas.
        </p>
        {remove.error ? (
          <p className="text-sm text-destructive" role="alert">
            {vaultErrorMessage(remove.error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(folder.id, {
                onSuccess: () => {
                  onDeleted?.();
                  onClose();
                },
              })
            }
          >
            {remove.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Senha: exclusão (auditada) ──────────────────────────────────────────────

export function DeleteVaultEntryDialog({
  entry,
  onClose,
}: {
  entry: VaultEntry;
  onClose: () => void;
}) {
  const remove = useDeleteVaultEntry();
  return (
    <Dialog open onClose={onClose} title="Excluir senha">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja excluir a senha{' '}
          <span className="font-medium text-foreground">{entry.name}</span>? A exclusão fica
          registrada no histórico do cofre.
        </p>
        {remove.error ? (
          <p className="text-sm text-destructive" role="alert">
            {vaultErrorMessage(remove.error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={() => remove.mutate(entry.id, { onSuccess: onClose })}
          >
            {remove.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Histórico (auditoria) ───────────────────────────────────────────────────

export function VaultLogsDialog({ vault, onClose }: { vault: Vault; onClose: () => void }) {
  const logsQ = useQuery(vaultLogsOptions(vault.id));

  return (
    <Dialog open onClose={onClose} title={`Histórico — ${vault.name}`} className="sm:max-w-2xl">
      {logsQ.isPending ? (
        <div aria-busy className="space-y-3 py-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-5 w-20 rounded-sm" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      ) : logsQ.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {vaultErrorMessage(logsQ.error)}
        </p>
      ) : logsQ.data.length === 0 ? (
        <EmptyState message="Nenhum evento registrado neste cofre" />
      ) : (
        <div className="overflow-hidden rounded-md border">
          <div className="max-h-[55vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-9 px-3 font-semibold">Data</th>
                  <th className="h-9 px-3 font-semibold">Usuário</th>
                  <th className="h-9 px-3 font-semibold">Ação</th>
                  <th className="h-9 px-3 font-semibold">Senha</th>
                </tr>
              </thead>
              <tbody>
                {logsQ.data.map((log) => (
                  <tr key={log.id} className="h-10 border-b border-divider last:border-0">
                    <td className="px-3 whitespace-nowrap text-muted-foreground tabular-nums">
                      {formatDateTimeBR(log.createdAt)}
                    </td>
                    <td className="max-w-36 truncate px-3" title={log.userName ?? undefined}>
                      {log.userName ?? '—'}
                    </td>
                    <td className="px-3 whitespace-nowrap">
                      <LogActionBadge action={log.action} />
                    </td>
                    <td className="max-w-48 truncate px-3" title={log.entryName}>
                      {log.entryName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Dialog>
  );
}
